/**
 * IIFE loader for the plain HTML / CDN install path.
 *
 * Hosts drop in a single tag, forever:
 *
 *   <script
 *     src="https://cdn.jsdelivr.net/npm/@mhosaic/feedback@1/dist/loader.min.js"
 *     data-key="pk_proj_…"
 *     data-endpoint="https://software-factory-…"
 *     defer
 *   ></script>
 *
 * The loader reads its own script tag's `data-key`/`data-endpoint`/`data-env`,
 * calls `createFeedback(…)` from `loader/index.ts`, and the deferred handle
 * does the rest (fetch manifest, inject the actual widget bundle with SRI).
 *
 * Once installed, hosts never touch this file again. Mhosaic publishes a new
 * Release row → every host's next page load picks up the new widget bundle.
 */

import { createFeedback } from './loader/index'
import type { FeedbackConfig } from './types'

interface QueuedCall {
  name: string
  args: unknown[]
}

interface WindowFeedback {
  (name: string, ...args: unknown[]): void
  q?: QueuedCall[]
}

declare global {
  interface Window {
    Feedback?: WindowFeedback
  }
}

/** Pure parser over a script tag's `dataset` — exported for unit testing. */
export function configFromDataset(d: {
  key?: string
  endpoint?: string
  env?: string
  qaSource?: string
  qaLocale?: string
}): FeedbackConfig | null {
  if (!d.key || !d.endpoint) return null
  const env = d.env as FeedbackConfig['env'] | undefined
  return {
    apiKey: d.key,
    endpoint: d.endpoint,
    ...(env !== undefined && { env }),
    ...(d.qaSource
      ? { qaMeter: { source: d.qaSource, ...(d.qaLocale ? { locale: d.qaLocale as 'fr' | 'en' } : {}) } }
      : {}),
  }
}

function readConfigFromScriptTag(): FeedbackConfig | null {
  const script = document.currentScript as HTMLScriptElement | null
  if (!script) return null
  return configFromDataset(script.dataset as Record<string, string | undefined>)
}

const config = readConfigFromScriptTag()
if (config) {
  const fb = createFeedback(config)
  // Drain any pre-queued calls a host may have set up before this script
  // loaded (Google-Analytics-style queue pattern: `window.Feedback = window.
  // Feedback || function(){(window.Feedback.q=window.Feedback.q||[]).push(
  // arguments)}; Feedback('identify', {...})`).
  const pending = window.Feedback?.q ?? []
  function dispatch(call: QueuedCall): void {
    switch (call.name) {
      case 'identify':
        fb.identify(call.args[0] as Parameters<typeof fb.identify>[0])
        break
      case 'setMetadata':
        fb.setMetadata(call.args[0] as Parameters<typeof fb.setMetadata>[0])
        break
      case 'open':
        fb.open(call.args[0] as Parameters<typeof fb.open>[0])
        break
      case 'show':
        fb.show()
        break
      case 'hide':
        fb.hide()
        break
      // submit and shutdown intentionally omitted from the queue API —
      // they have return values that the queue pattern can't surface
      // synchronously.
      default:
        // eslint-disable-next-line no-console
        console.warn('[mhosaic-feedback] unknown queue call:', call.name)
    }
  }
  for (const c of pending) dispatch(c)

  const fn = Object.assign(
    function Feedback(name: string, ...args: unknown[]): void {
      dispatch({ name, args })
    },
    { q: [] as QueuedCall[] },
  ) as WindowFeedback
  window.Feedback = fn
}
